'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { MapPin, Globe, Car, Home, Star, MessageSquare, CalendarPlus, Flag, ChevronLeft, ShieldCheck, Clock, CheckCircle, AlertTriangle, DollarSign } from 'lucide-react';
import VettingBadge from '@/components/ui/VettingBadge';
import StarRating from '@/components/ui/StarRating';
import AppImage from '@/components/ui/AppImage';
import BookingRequestModal from './BookingRequestModal';
import { createClient } from '@/lib/supabase/client';

// Backend integration point: GET /api/workers/:id
const worker = {
  id: 'worker-001',
  displayName: 'Thandi M.',
  vetted: true,
  suburb: 'Waterkloof Ridge',
  category: 'Elder Care & Companionship',
  age: 34,
  hasLicense: true,
  hasVehicle: true,
  liveIn: true,
  relocate: false,
  distanceKm: 15,
  rate: 'R180 / hour',
  languages: ['English', 'Zulu', 'Northern Sotho'],
  tags: ["Alzheimer\'s care", 'Dementia support', 'Companionship', 'Mobility support', 'Medication reminders'],
  qualifications: 'Diploma in Health Sciences (Tshwane University) · First Aid Level 2 · Dementia Care Certificate (2022)',
  bio: `I am a dedicated care specialist with over 8 years of experience supporting families through Alzheimer's and dementia journeys in Gauteng. I believe that dignity, patience, and genuine connection are the foundations of great elder care.

I have worked with families in Waterkloof Ridge, Lynnwood, and Centurion — providing everything from daily companionship and light household assistance to full-time live-in care. I hold a First Aid Level 2 certificate and have completed specialised dementia care training.

I am calm under pressure, consistent with medication schedules, and I communicate openly with family members. I treat every client as if they were my own family.`,
  rating: 4.9,
  reviewCount: 23,
  photo: "https://img.rocket.new/generatedImages/rocket_gen_img_13604096a-1772154060305.png",
  photoAlt: 'Smiling woman with natural hair in professional attire, care worker',profileViews: 312,responseRate: 97,avgResponseTime: '< 2 hours',memberSince: 'March 2024',
  availability: {
    Mon: { available: true, hours: '07:00–17:00' },
    Tue: { available: true, hours: '07:00–17:00' },
    Wed: { available: true, hours: '07:00–17:00' },
    Thu: { available: true, hours: '07:00–17:00' },
    Fri: { available: true, hours: '07:00–15:00' },
    Sat: { available: false, hours: null },
    Sun: { available: false, hours: null }
  }
};

interface DBReview {
  id: string;
  reviewer_id: string;
  rating_overall: number;
  rating_punctuality: number;
  rating_quality: number;
  rating_attitude: number;
  comment: string | null;
  created_at: string;
  reviewer_name?: string;
}

interface ReviewSummary {
  avgOverall: number;
  avgPunctuality: number;
  avgQuality: number;
  avgAttitude: number;
  count: number;
}

function avg(arr: number[]) {
  if (!arr.length) return 0;
  return Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10;
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function WorkerProfileContent() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'about' | 'availability' | 'reviews'>('about');
  const [dbReviews, setDbReviews] = useState<DBReview[]>([]);
  const [summary, setSummary] = useState<ReviewSummary | null>(null);
  const [loadingReviews, setLoadingReviews] = useState(false);

  const days = Object.entries(worker.availability);

  useEffect(() => {
    if (activeTab !== 'reviews') return;
    setLoadingReviews(true);
    const supabase = createClient();
    // In production: replace 'worker-001' with the actual worker's user_profiles UUID
    // For now we load all reviews to show the feature working
    supabase
      .from('reviews')
      .select('id, reviewer_id, rating_overall, rating_punctuality, rating_quality, rating_attitude, comment, created_at')
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        const rows = (data ?? []) as DBReview[];
        setDbReviews(rows);
        if (rows.length > 0) {
          setSummary({
            avgOverall: avg(rows.map((r) => r.rating_overall)),
            avgPunctuality: avg(rows.map((r) => r.rating_punctuality)),
            avgQuality: avg(rows.map((r) => r.rating_quality)),
            avgAttitude: avg(rows.map((r) => r.rating_attitude)),
            count: rows.length,
          });
        } else {
          setSummary(null);
        }
        setLoadingReviews(false);
      });
  }, [activeTab]);

  // Fallback static reviews when no DB reviews exist yet
  const staticReviews = [
    { id: 'review-001', reviewerName: 'Sandra B.', rating: 5, date: '14 Apr 2026', text: "Thandi has been caring for my mother for 6 months now. She is patient, warm, and incredibly professional. My mother has advanced Alzheimer's and Thandi handles every difficult moment with grace. I cannot recommend her highly enough.", workerReply: "Thank you so much, Sandra. It has been a privilege caring for your mother. She is a wonderful woman." },
    { id: 'review-002', reviewerName: 'James O.', rating: 5, date: '02 Mar 2026', text: "Excellent care worker. Very punctual, great communicator, and my father genuinely looks forward to her visits. Medication schedules are always followed.", workerReply: null },
    { id: 'review-003', reviewerName: 'Priya N.', rating: 4, date: '18 Feb 2026', text: "Very good experience overall. Thandi is knowledgeable about dementia care and handled a difficult week with my dad with real composure. Slightly late once but communicated ahead of time.", workerReply: null },
    { id: 'review-004', reviewerName: 'Kobus V.', rating: 5, date: '05 Jan 2026', text: "We have used several care workers before and Thandi is by far the best. She genuinely cares about the people she looks after. Will continue using her services.", workerReply: "Thank you Kobus, it means the world. Please give my regards to your family." },
  ];

  const displayReviews = dbReviews.length > 0 ? dbReviews : null;
  const displaySummary = summary ?? {
    avgOverall: worker.rating,
    avgPunctuality: 4.8,
    avgQuality: 4.9,
    avgAttitude: 5.0,
    count: worker.reviewCount,
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-8">
      {/* Back */}
      <Link href="/find-help-search-discovery" className="inline-flex items-center gap-1.5 text-sm font-medium mb-6 hover:underline" style={{ color: 'var(--muted-foreground)' }}>
        <ChevronLeft size={16} />
        Back to search results
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-5">
          {/* Profile header card */}
          <div className="card p-6">
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="relative flex-shrink-0">
                <AppImage
                  src={worker.photo}
                  alt={worker.photoAlt}
                  width={120}
                  height={120}
                  className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover" />
                {worker.vetted &&
                  <div className="absolute -bottom-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ background: 'var(--vetted-color)' }}>
                    <CheckCircle size={16} color="white" />
                  </div>
                }
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-start gap-3 mb-2">
                  <h1 className="text-2xl font-extrabold" style={{ color: 'var(--navy)' }}>{worker.displayName}</h1>
                  <VettingBadge vetted={worker.vetted} size="md" />
                </div>

                <p className="text-sm font-semibold mb-2" style={{ color: 'var(--primary)' }}>{worker.category}</p>

                <div className="flex flex-wrap gap-4 text-sm mb-3" style={{ color: 'var(--muted-foreground)' }}>
                  <span className="flex items-center gap-1.5"><MapPin size={14} />{worker.suburb}, Gauteng</span>
                  <span className="flex items-center gap-1.5"><Globe size={14} />{worker.languages.join(', ')}</span>
                  {worker.hasLicense && <span className="flex items-center gap-1.5"><Car size={14} style={{ color: 'var(--success)' }} />Driver&apos;s licence</span>}
                  {worker.liveIn && <span className="flex items-center gap-1.5"><Home size={14} style={{ color: 'var(--success)' }} />Live-in available</span>}
                  {worker.rate && <span className="flex items-center gap-1.5 font-semibold" style={{ color: 'var(--primary)' }}><DollarSign size={14} style={{ color: 'var(--primary)' }} />{worker.rate}</span>}
                </div>

                <div className="flex items-center gap-4">
                  <StarRating rating={displaySummary.avgOverall} count={displaySummary.count} size={15} />
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Member since {worker.memberSince}</span>
                </div>
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4 mt-5 pt-5 border-t border-border">
              {[
                { label: 'Response rate', value: `${worker.responseRate}%`, icon: <CheckCircle size={16} style={{ color: 'var(--success)' }} /> },
                { label: 'Avg response', value: worker.avgResponseTime, icon: <Clock size={16} style={{ color: 'var(--primary)' }} /> },
                { label: 'Profile views', value: worker.profileViews.toString(), icon: <Star size={16} style={{ color: 'var(--amber)' }} /> },
              ].map((stat) =>
                <div key={`stat-${stat.label}`} className="text-center">
                  <div className="flex justify-center mb-1">{stat.icon}</div>
                  <div className="font-bold text-sm tabular-nums" style={{ color: 'var(--navy)' }}>{stat.value}</div>
                  <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{stat.label}</div>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="card overflow-hidden">
            <div className="flex border-b border-border">
              {(['about', 'availability', 'reviews'] as const).map((tab) =>
                <button
                  key={`tab-${tab}`}
                  onClick={() => setActiveTab(tab)}
                  className="flex-1 px-4 py-3 text-sm font-semibold capitalize transition-all duration-150 relative"
                  style={{ color: activeTab === tab ? 'var(--primary)' : 'var(--muted-foreground)' }}>
                  {tab === 'reviews' ? `Reviews (${displaySummary.count})` : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  {activeTab === tab &&
                    <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: 'var(--primary)' }} />
                  }
                </button>
              )}
            </div>

            <div className="p-6">
              {activeTab === 'about' &&
                <div className="space-y-6">
                  <div>
                    <h3 className="font-bold text-sm mb-3" style={{ color: 'var(--navy)' }}>About Thandi</h3>
                    <div className="text-sm leading-relaxed whitespace-pre-line" style={{ color: 'var(--foreground)' }}>
                      {worker.bio}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-bold text-sm mb-3" style={{ color: 'var(--navy)' }}>Qualifications</h3>
                    <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{worker.qualifications}</p>
                  </div>

                  <div>
                    <h3 className="font-bold text-sm mb-3" style={{ color: 'var(--navy)' }}>Specialisations</h3>
                    <div className="flex flex-wrap gap-2">
                      {worker.tags.map((tag) =>
                        <span key={`profile-tag-${tag}`} className="text-xs px-3 py-1.5 rounded-full font-medium" style={{ background: 'var(--orange-pale)', color: 'var(--primary)' }}>
                          {tag}
                        </span>
                      )}
                    </div>
                  </div>

                  {worker.vetted &&
                    <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: 'var(--vetted-bg)' }}>
                      <ShieldCheck size={20} style={{ color: 'var(--vetted-color)' }} className="flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-bold mb-0.5" style={{ color: 'var(--vetted-color)' }}>Fully vetted by Handlenders</p>
                        <p className="text-xs" style={{ color: 'var(--vetted-color)', opacity: 0.8 }}>
                          SA ID verified · Police clearance confirmed · Telephonic screening completed
                        </p>
                      </div>
                    </div>
                  }
                </div>
              }

              {activeTab === 'availability' &&
                <div>
                  <h3 className="font-bold text-sm mb-4" style={{ color: 'var(--navy)' }}>Weekly availability</h3>
                  <div className="space-y-2">
                    {days.map(([day, info]) =>
                      <div key={`avail-row-${day}`} className="flex items-center justify-between py-2.5 px-3 rounded-xl" style={{ background: info.available ? 'var(--success-bg)' : 'var(--muted)' }}>
                        <span className="text-sm font-semibold" style={{ color: info.available ? 'var(--success)' : 'var(--muted-foreground)' }}>{day}</span>
                        <span className="text-sm" style={{ color: info.available ? 'var(--success)' : 'var(--muted-foreground)' }}>
                          {info.available ? info.hours : 'Not available'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 p-3 rounded-xl flex items-start gap-2" style={{ background: 'var(--orange-pale)' }}>
                    <AlertTriangle size={15} style={{ color: 'var(--primary)' }} className="flex-shrink-0 mt-0.5" />
                    <p className="text-xs" style={{ color: 'var(--primary)' }}>
                      Availability is a guide only. Confirm specific dates when booking.
                    </p>
                  </div>
                </div>
              }

              {activeTab === 'reviews' &&
                <div className="space-y-5">
                  {/* Summary card */}
                  <div className="rounded-xl p-4" style={{ background: 'var(--muted)' }}>
                    <div className="flex flex-col sm:flex-row gap-5">
                      {/* Overall score */}
                      <div className="text-center sm:border-r sm:border-border sm:pr-5 flex-shrink-0">
                        <div className="text-4xl font-extrabold tabular-nums" style={{ color: 'var(--navy)' }}>{displaySummary.avgOverall}</div>
                        <StarRating rating={displaySummary.avgOverall} showCount={false} size={16} />
                        <div className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{displaySummary.count} review{displaySummary.count !== 1 ? 's' : ''}</div>
                      </div>

                      {/* Category breakdown */}
                      <div className="flex-1 space-y-2">
                        {[
                          { label: 'Punctuality', value: displaySummary.avgPunctuality },
                          { label: 'Quality of Service', value: displaySummary.avgQuality },
                          { label: 'Attitude', value: displaySummary.avgAttitude },
                        ].map((cat) => (
                          <div key={cat.label} className="flex items-center gap-3">
                            <span className="text-xs w-32 flex-shrink-0" style={{ color: 'var(--muted-foreground)' }}>{cat.label}</span>
                            <div className="flex-1 h-1.5 rounded-full" style={{ background: 'var(--border)' }}>
                              <div className="h-full rounded-full transition-all" style={{ width: `${(cat.value / 5) * 100}%`, background: 'var(--amber)' }} />
                            </div>
                            <span className="text-xs font-semibold tabular-nums w-6 text-right" style={{ color: 'var(--navy)' }}>{cat.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Review list */}
                  {loadingReviews ? (
                    <div className="py-8 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>Loading reviews…</div>
                  ) : displayReviews ? (
                    displayReviews.map((review) => (
                      <div key={review.id} className="border-b border-border pb-5 last:border-0 last:pb-0">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className="text-sm font-bold" style={{ color: 'var(--navy)' }}>
                              {review.reviewer_name ?? 'Verified customer'}
                            </span>
                            <div className="mt-0.5"><StarRating rating={review.rating_overall} showCount={false} size={13} /></div>
                          </div>
                          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{formatDate(review.created_at)}</span>
                        </div>
                        {/* Category mini-scores */}
                        <div className="flex flex-wrap gap-2 mb-2">
                          {[
                            { label: 'Punctuality', value: review.rating_punctuality },
                            { label: 'Quality', value: review.rating_quality },
                            { label: 'Attitude', value: review.rating_attitude },
                          ].map((c) => (
                            <span key={c.label} className="text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1" style={{ background: 'var(--orange-pale)', color: 'var(--primary)' }}>
                              <Star size={10} className="fill-current" style={{ color: 'var(--amber)' }} />
                              {c.label} {c.value}
                            </span>
                          ))}
                        </div>
                        {review.comment && (
                          <p className="text-sm leading-relaxed" style={{ color: 'var(--foreground)' }}>{review.comment}</p>
                        )}
                      </div>
                    ))
                  ) : (
                    // Fallback static reviews
                    staticReviews.map((review) => (
                      <div key={review.id} className="border-b border-border pb-5 last:border-0 last:pb-0">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className="text-sm font-bold" style={{ color: 'var(--navy)' }}>{review.reviewerName}</span>
                            <div className="mt-0.5"><StarRating rating={review.rating} showCount={false} size={13} /></div>
                          </div>
                          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{review.date}</span>
                        </div>
                        <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--foreground)' }}>{review.text}</p>
                        {review.workerReply &&
                          <div className="ml-4 pl-3 border-l-2 rounded-r-lg py-2 pr-3" style={{ borderColor: 'var(--primary)', background: 'var(--orange-pale)' }}>
                            <p className="text-xs font-semibold mb-0.5" style={{ color: 'var(--primary)' }}>Thandi M. replied:</p>
                            <p className="text-xs" style={{ color: 'var(--foreground)' }}>{review.workerReply}</p>
                          </div>
                        }
                      </div>
                    ))
                  )}
                </div>
              }
            </div>
          </div>
        </div>

        {/* Sticky booking sidebar */}
        <div className="lg:col-span-1">
          <div className="card p-5 sticky top-24 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold" style={{ color: 'var(--navy)' }}>Book Thandi M.</h3>
              <VettingBadge vetted={worker.vetted} size="sm" />
            </div>

            {/* Privacy note */}
            <div className="rounded-xl p-3 flex items-start gap-2" style={{ background: 'var(--muted)' }}>
              <ShieldCheck size={14} style={{ color: 'var(--navy)' }} className="flex-shrink-0 mt-0.5" />
              <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                Your phone number and email are never shared. All communication stays on the Handlenders Talent Network.
              </p>
            </div>

            {/* Booking fee */}
            <div className="rounded-xl p-3 border-2" style={{ borderColor: 'var(--primary)', background: 'var(--orange-pale)' }}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold" style={{ color: 'var(--primary)' }}>Booking confirmation fee</span>
                <span className="font-extrabold tabular-nums" style={{ color: 'var(--primary)' }}>R100</span>
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--primary)', opacity: 0.8 }}>Paid only when you confirm the booking</p>
            </div>

            <button
              onClick={() => setBookingOpen(true)}
              className="btn-primary w-full">
              <CalendarPlus size={18} />
              Request a Booking
            </button>

            <Link href="/sign-up-login?action=message" className="btn-outline w-full text-center">
              <MessageSquare size={18} />
              Send a Message
            </Link>

            {/* Address note */}
            <div className="rounded-xl p-3 flex items-start gap-2" style={{ background: 'var(--warning-bg)' }}>
              <MapPin size={14} style={{ color: 'var(--warning)' }} className="flex-shrink-0 mt-0.5" />
              <p className="text-xs leading-relaxed" style={{ color: 'var(--warning)' }}>
                Exact address is only shared 3 hours before your confirmed booking — never in chat.
              </p>
            </div>

            {/* Report */}
            <button className="flex items-center gap-1.5 text-xs w-full justify-center py-2 hover:underline transition-colors" style={{ color: 'var(--muted-foreground)' }}>
              <Flag size={12} />
              Report this profile
            </button>
          </div>
        </div>
      </div>

      {bookingOpen && <BookingRequestModal onClose={() => setBookingOpen(false)} workerName={worker.displayName} />}
    </div>
  );
}